import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as authApi from '../api/auth';
import { getToken } from '../api/client';

/**
 * AuthContext — holds the current user and exposes login/logout.
 *
 * Shape:
 *   user       : null | { id, email, isSuperadmin, role, createdAt, raw }
 *   isLoading  : true until the initial /me probe resolves (or there is no token)
 *   error      : last login error message (cleared on new attempt)
 *   login(email, password)  -> user  (throws on failure)
 *   logout()                -> void
 *   refresh()               -> re-fetch /me
 */
export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(() => Boolean(getToken()));
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Initial /me probe if a token exists. If /me 401s, client.js clears the token
  // silently (skipAuthRedirect) and we just leave user=null.
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const u = await authApi.me();
        if (!cancelled && mounted.current) setUser(u);
      } catch {
        if (!cancelled && mounted.current) setUser(null);
      } finally {
        if (!cancelled && mounted.current) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const u = await authApi.login(email, password);
      if (mounted.current) setUser(u);
      return u;
    } catch (err) {
      const msg = mapLoginError(err);
      if (mounted.current) setError(msg);
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    authApi.logout();
    setUser(null);
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const u = await authApi.me();
      if (mounted.current) setUser(u);
      return u;
    } catch {
      if (mounted.current) setUser(null);
      return null;
    }
  }, []);

  const value = useMemo(
    () => ({ user, isLoading, error, login, logout, refresh }),
    [user, isLoading, error, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function mapLoginError(err) {
  if (err && err.status === 401) return 'Неверный email или пароль';
  if (err && err.message) return err.message;
  return 'Не удалось войти. Попробуйте ещё раз.';
}
